import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sword, BookOpen, Shield, Target, Skull, ArrowLeft, Sparkles, Globe, Palette } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import soundManager from "@/utils/SoundManager";
import ambientMusic from "@/utils/AmbientMusic";
import { t, tf, getStoredLang } from "@/utils/i18n";

const LANGUAGES = [
  { id: "it", name: "Italiano", flag: "IT" },
  { id: "en", name: "English", flag: "EN" },
  { id: "es", name: "Espanol", flag: "ES" },
  { id: "fr", name: "Francais", flag: "FR" },
  { id: "de", name: "Deutsch", flag: "DE" },
  { id: "pt", name: "Portugues", flag: "PT" },
  { id: "ja", name: "Japanese", flag: "JP" },
  { id: "zh", name: "Chinese", flag: "CN" },
  { id: "ko", name: "Korean", flag: "KR" },
  { id: "ru", name: "Russian", flag: "RU" },
  { id: "pl", name: "Polski", flag: "PL" },
  { id: "nl", name: "Nederlands", flag: "NL" },
];

const CLASSES = [
  { id: "warrior", icon: Sword, color: "#dc2626" },
  { id: "mage", icon: Sparkles, color: "#3b82f6" },
  { id: "rogue", icon: Target, color: "#a855f7" },
  { id: "ranger", icon: Shield, color: "#10b981" },
  { id: "necromancer", icon: Skull, color: "#6366f1" },
];

const SETTINGS = [
  { id: "fantasy" },
  { id: "dark_fantasy" },
  { id: "sci_fi" },
  { id: "horror" },
  { id: "steampunk" },
  { id: "post_apocalyptic" },
  { id: "mythology" },
];

const IMAGE_STYLES = [
  { id: "anime", label_en: "Anime", label_it: "Anime", emoji: "🎌", desc_en: "Japanese manga, cel-shaded", desc_it: "Manga giapponese, cel-shaded" },
  { id: "realistic", label_en: "Realistic", label_it: "Realistico", emoji: "🖼️", desc_en: "Photorealistic, cinematic", desc_it: "Fotorealistico, cinematografico" },
  { id: "oil_painting", label_en: "Oil Painting", label_it: "Pittura ad Olio", emoji: "🎨", desc_en: "Classical brushstrokes", desc_it: "Pennellate classiche" },
  { id: "pixel_art", label_en: "Pixel Art", label_it: "Pixel Art", emoji: "👾", desc_en: "Retro 16-bit JRPG", desc_it: "JRPG retro 16-bit" },
  { id: "ghibli", label_en: "Studio Ghibli", label_it: "Studio Ghibli", emoji: "🌿", desc_en: "Soft watercolor, whimsical", desc_it: "Acquerello morbido, fiabesco" },
  { id: "dark_fantasy", label_en: "Dark Fantasy", label_it: "Dark Fantasy", emoji: "🌑", desc_en: "Gothic, Soulsborne mood", desc_it: "Gotico, atmosfera Soulsborne" },
];

export default function CharacterCreation() {
  const [step, setStep] = useState(1);
  const [charName, setCharName] = useState("");
  const [charClass, setCharClass] = useState("");
  const [setting, setSetting] = useState("");
  const [language, setLanguage] = useState("it");
  const [imageStyle, setImageStyle] = useState("anime");
  const [creating, setCreating] = useState(false);
  // iter 74 — heroic overture for character creation
  useEffect(() => { ambientMusic.play('character_creation'); }, []);
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const uiLang = getStoredLang();

  const handleCreate = async () => {
    if (!charName.trim() || !charClass || !setting) {
      toast.error("Please complete all fields");
      return;
    }
    setCreating(true);
    soundManager.playClick();
    try {
      const res = await axios.post(`${API}/game/new`, {
        character_name: charName.trim(),
        character_class: charClass,
        setting: setting,
        language: language,
        image_style: imageStyle,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t(uiLang, 'begin_adventure') + "!");
      soundManager.playNewItem();
      navigate(`/game/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create game");
    } finally {
      setCreating(false);
    }
  };

  const goNext = (validation, errorMsg) => {
    if (!validation) { toast.error(errorMsg); return; }
    soundManager.playClick();
    setStep(s => s + 1);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c] relative" data-testid="character-creation">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0c] via-[#1a1a1d] to-[#0a0a0c]" />

      <div className="relative z-10 max-w-3xl mx-auto px-3 sm:px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Button variant="ghost" onClick={() => { soundManager.playClick(); step > 1 ? setStep(step - 1) : navigate("/"); }} className="text-[#94a3b8] hover:text-[#cfb53b] h-8 w-8 p-0 sm:h-auto sm:w-auto sm:p-2" data-testid="back-button">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-heading text-lg sm:text-2xl lg:text-3xl font-bold text-[#cfb53b]">{t(uiLang, 'create_legend')}</h1>
            <p className="text-[#94a3b8] text-xs sm:text-sm">{tf(uiLang, 'step_of', [step, 5])}</p>
          </div>
        </div>

        {/* Progress */}
        <div className="flex gap-1.5 sm:gap-2 mb-6 sm:mb-8">
          {[1, 2, 3, 4, 5].map(s => (
            <div key={s} className={`h-1 flex-1 rounded-full transition-all duration-500 ${s <= step ? 'bg-[#cfb53b]' : 'bg-[#2d3748]'}`} />
          ))}
        </div>

        {/* Step 1: Language + Name */}
        {step === 1 && (
          <div className="animate-fadeInUp">
            <h2 className="font-heading text-base sm:text-xl text-[#e2e8f0] mb-4 sm:mb-6">{t(uiLang, 'choose_lang_name')}</h2>
            <div className="max-w-md space-y-4 sm:space-y-5">
              <div>
                <Label className="text-[#cfb53b] font-heading text-sm flex items-center gap-2">
                  <Globe size={14} /> {t(uiLang, 'story_language')}
                </Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="mt-2 bg-[#0a0a0c] border-[#cfb53b]/30 text-[#e2e8f0]" data-testid="language-select">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1d] border-[#cfb53b]/30">
                    {LANGUAGES.map(l => (
                      <SelectItem key={l.id} value={l.id} className="text-[#e2e8f0] focus:bg-[#cfb53b]/10 focus:text-[#cfb53b]" data-testid={`lang-${l.id}`}>
                        <span className="flex items-center gap-2">
                          <span className="text-xs font-mono bg-[#cfb53b]/10 px-1.5 py-0.5 rounded text-[#cfb53b]">{l.flag}</span>
                          {l.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#64748b] mt-1">{t(uiLang, 'lang_hint')}</p>
              </div>
              <div>
                <Label htmlFor="charName" className="text-[#cfb53b] font-heading text-sm">{t(uiLang, 'character_name')}</Label>
                <Input
                  id="charName"
                  data-testid="character-name-input"
                  value={charName}
                  onChange={(e) => setCharName(e.target.value)}
                  placeholder={t(uiLang, 'enter_name')}
                  className="mt-2 bg-[#0a0a0c] border-[#cfb53b]/30 text-[#e2e8f0] placeholder:text-[#64748b] focus:border-[#cfb53b] text-base sm:text-lg py-3"
                  maxLength={30}
                />
              </div>
            </div>
            <Button
              data-testid="step1-next"
              onClick={() => goNext(charName.trim(), "Enter a name")}
              className="mt-6 sm:mt-8 bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading font-bold px-8 w-full sm:w-auto"
            >
              {t(uiLang, 'continue_btn')}
            </Button>
          </div>
        )}

        {/* Step 2: Class */}
        {step === 2 && (
          <div className="animate-fadeInUp">
            <h2 className="font-heading text-base sm:text-xl text-[#e2e8f0] mb-4 sm:mb-6">{t(uiLang, 'choose_class')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 stagger-children">
              {CLASSES.map(cls => {
                const Icon = cls.icon;
                const selected = charClass === cls.id;
                return (
                  <div
                    key={cls.id}
                    data-testid={`class-${cls.id}`}
                    onClick={() => { setCharClass(cls.id); soundManager.playClick(); }}
                    className={`p-4 sm:p-5 rounded-lg border-2 cursor-pointer transition-all animate-fadeInUp active:scale-95 ${
                      selected
                        ? 'border-[#cfb53b] bg-[#cfb53b]/10 shadow-[0_0_15px_rgba(207,181,59,0.2)]'
                        : 'border-[#2d3748] bg-[#1a1a1d]/60 hover:border-[#cfb53b]/40'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Icon size={22} style={{ color: cls.color }} />
                      <span className="font-heading text-base sm:text-lg text-[#e2e8f0]">{t(uiLang, `class_${cls.id}`)}</span>
                    </div>
                    <p className="text-xs sm:text-sm text-[#94a3b8]">{t(uiLang, `class_${cls.id}_desc`)}</p>
                    <p className="text-[10px] sm:text-xs text-[#64748b] mt-1.5 border-t border-[#2d3748] pt-1.5">{t(uiLang, 'starts_with')}: {t(uiLang, `class_${cls.id}_starting`)}</p>
                  </div>
                );
              })}
            </div>
            <Button
              data-testid="step2-next"
              onClick={() => goNext(charClass, "Choose a class")}
              className="mt-6 sm:mt-8 bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading font-bold px-8 w-full sm:w-auto"
            >
              {t(uiLang, 'continue_btn')}
            </Button>
          </div>
        )}

        {/* Step 3: Setting */}
        {step === 3 && (
          <div className="animate-fadeInUp">
            <h2 className="font-heading text-base sm:text-xl text-[#e2e8f0] mb-4 sm:mb-6">{t(uiLang, 'choose_world')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 stagger-children">
              {SETTINGS.map(s => {
                const selected = setting === s.id;
                return (
                  <div
                    key={s.id}
                    data-testid={`setting-${s.id}`}
                    onClick={() => { setSetting(s.id); soundManager.playClick(); }}
                    className={`p-4 sm:p-5 rounded-lg border-2 cursor-pointer transition-all animate-fadeInUp active:scale-95 ${
                      selected
                        ? 'border-[#cfb53b] bg-[#cfb53b]/10 shadow-[0_0_15px_rgba(207,181,59,0.2)]'
                        : 'border-[#2d3748] bg-[#1a1a1d]/60 hover:border-[#cfb53b]/40'
                    }`}
                  >
                    <span className="font-heading text-base sm:text-lg text-[#e2e8f0]">{t(uiLang, `setting_${s.id}`)}</span>
                    <p className="text-xs sm:text-sm text-[#94a3b8] mt-1">{t(uiLang, `setting_${s.id}_desc`)}</p>
                  </div>
                );
              })}
            </div>
            <Button
              data-testid="step3-next"
              onClick={() => goNext(setting, "Choose a setting")}
              className="mt-6 sm:mt-8 bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading font-bold px-8 w-full sm:w-auto"
            >
              {t(uiLang, 'continue_btn')}
            </Button>
          </div>
        )}

        {/* Step 4: Image Style */}
        {step === 4 && (
          <div className="animate-fadeInUp">
            <h2 className="font-heading text-base sm:text-xl text-[#e2e8f0] mb-1 flex items-center gap-2">
              <Palette size={18} className="text-[#cfb53b]" />
              {uiLang.startsWith("it") ? "Stile Artistico" : "Art Style"}
            </h2>
            <p className="text-xs sm:text-sm text-[#94a3b8] mb-4 sm:mb-6">
              {uiLang.startsWith("it")
                ? "Scegli come l'IA genererà le immagini della tua avventura. Potrai cambiarlo più tardi nelle impostazioni."
                : "Choose how the AI will generate your adventure's images. You can change it later in settings."}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 stagger-children">
              {IMAGE_STYLES.map(s => {
                const selected = imageStyle === s.id;
                return (
                  <div
                    key={s.id}
                    data-testid={`style-${s.id}`}
                    onClick={() => { setImageStyle(s.id); soundManager.playClick(); }}
                    className={`p-3 sm:p-4 rounded-lg border-2 cursor-pointer transition-all animate-fadeInUp active:scale-95 ${
                      selected
                        ? 'border-[#cfb53b] bg-[#cfb53b]/10 shadow-[0_0_15px_rgba(207,181,59,0.2)]'
                        : 'border-[#2d3748] bg-[#1a1a1d]/60 hover:border-[#cfb53b]/40'
                    }`}
                  >
                    <div className="text-3xl sm:text-4xl mb-1 text-center">{s.emoji}</div>
                    <p className="font-heading text-sm sm:text-base text-[#e2e8f0] text-center">{uiLang.startsWith("it") ? s.label_it : s.label_en}</p>
                    <p className="text-[10px] sm:text-xs text-[#94a3b8] mt-0.5 text-center">{uiLang.startsWith("it") ? s.desc_it : s.desc_en}</p>
                  </div>
                );
              })}
            </div>
            <Button
              data-testid="step4-next"
              onClick={() => goNext(imageStyle, "Choose a style")}
              className="mt-6 sm:mt-8 bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading font-bold px-8 w-full sm:w-auto"
            >
              {t(uiLang, 'continue_btn')}
            </Button>
          </div>
        )}

        {/* Step 5: Summary & Create */}
        {step === 5 && (
          <div className="animate-fadeInUp">
            <h2 className="font-heading text-base sm:text-xl text-[#e2e8f0] mb-4 sm:mb-6">{t(uiLang, 'confirm_legend')}</h2>
            <div className="bg-[#1a1a1d]/80 border border-[#cfb53b]/20 rounded-lg p-4 sm:p-6 space-y-3 sm:space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <span className="text-[10px] sm:text-xs text-[#94a3b8] font-heading">{t(uiLang, 'name').toUpperCase()}</span>
                  <p className="text-[#cfb53b] font-heading text-sm sm:text-lg">{charName}</p>
                </div>
                <div>
                  <span className="text-[10px] sm:text-xs text-[#94a3b8] font-heading">{t(uiLang, 'class_label').toUpperCase()}</span>
                  <p className="text-[#e2e8f0] font-heading text-sm sm:text-lg">{t(uiLang, `class_${charClass}`)}</p>
                </div>
                <div>
                  <span className="text-[10px] sm:text-xs text-[#94a3b8] font-heading">{t(uiLang, 'world').toUpperCase()}</span>
                  <p className="text-[#e2e8f0] font-heading text-sm sm:text-lg">{t(uiLang, `setting_${setting}`)}</p>
                </div>
                <div>
                  <span className="text-[10px] sm:text-xs text-[#94a3b8] font-heading">{t(uiLang, 'language').toUpperCase()}</span>
                  <p className="text-[#e2e8f0] font-heading text-sm sm:text-lg">{LANGUAGES.find(l => l.id === language)?.name}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-[10px] sm:text-xs text-[#94a3b8] font-heading">{uiLang.startsWith("it") ? "STILE ARTISTICO" : "ART STYLE"}</span>
                  <p className="text-[#e2e8f0] font-heading text-sm sm:text-lg">
                    {IMAGE_STYLES.find(s => s.id === imageStyle)?.emoji}{" "}
                    {uiLang.startsWith("it")
                      ? IMAGE_STYLES.find(s => s.id === imageStyle)?.label_it
                      : IMAGE_STYLES.find(s => s.id === imageStyle)?.label_en}
                  </p>
                </div>
              </div>
              <div className="pt-3 border-t border-[#2d3748]">
                <span className="text-[10px] sm:text-xs text-[#94a3b8] font-heading">{t(uiLang, 'starting_equipment').toUpperCase()}</span>
                <p className="text-xs sm:text-sm text-[#e2e8f0] mt-1">{t(uiLang, `class_${charClass}_starting`)}</p>
              </div>
            </div>
            <Button
              data-testid="create-game-button"
              onClick={handleCreate}
              disabled={creating}
              className="mt-6 sm:mt-8 bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading font-bold px-10 py-3 text-base sm:text-lg w-full"
            >
              {creating ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-[#0a0a0c] border-t-transparent rounded-full animate-spin" />
                  {t(uiLang, 'forging_world')}
                </span>
              ) : t(uiLang, 'begin_adventure')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
